// Listen – die EINE Konvention für alle Listen-Aktionen (Oberfläche, API, Assistent).
//
//   Eingabe   { q?, sort?: { by, dir }, page?: { limit, cursor }, …Filter der Aktion }
//   Ausgabe   { rows, nextCursor, total? }
//
// Geblättert wird per Keyset (kein OFFSET): `nextCursor` der Antwort unverändert als `page.cursor` der nächsten
// Anfrage senden; null = Ende. Der Cursor gilt nur für dieselbe Sortierung und dieselben Filter. Auf dem Server
// baut keysetPage() aus '@kernel' die Abfrage dazu.
//
//   export const itemList = defineContract({
//     input: listInput({ sortBy: ['name', 'sku'], defaultSort: { by: 'name' }, filters: { groupId: zId.optional() } }),
//     output: listOutput(itemRow),
//     scope: 'any', …
//   })
import { z } from 'zod'

export const LIST_DEFAULT_LIMIT = 50
export const LIST_MAX_LIMIT = 200

export const SORT_DIRECTIONS = ['asc', 'desc'] as const
export type SortDirection = (typeof SORT_DIRECTIONS)[number]

/** Suchtext einer Liste: beschnitten, leer = kein Filter. */
export const zListQuery = z
  .string()
  .trim()
  .max(200)
  .optional()
  .transform((value) => (value ? value : undefined))

/** Seite: höchstens 200 Zeilen; `cursor` = `nextCursor` der vorigen Antwort. */
export const zListPage = z
  .strictObject({
    limit: z.int().min(1).max(LIST_MAX_LIMIT).default(LIST_DEFAULT_LIMIT),
    cursor: z.string().min(1).max(2000).nullish(),
  })
  .default({ limit: LIST_DEFAULT_LIMIT })

export interface ListPage {
  limit: number
  cursor?: string | null
}

export interface ListSort<K extends string = string> {
  by: K
  dir: SortDirection
}

export interface ListInputSpec<K extends string, F extends z.ZodRawShape> {
  /** erlaubte Sortierschlüssel (Namen der Ausgabefelder) – der erste ist der Standard, wenn defaultSort fehlt */
  sortBy: readonly [K, ...K[]]
  defaultSort?: { by: NoInfer<K>; dir?: SortDirection }
  /** Filter der Aktion (alle optional halten) */
  filters?: F
}

/** Eingabeschema einer Listen-Aktion: q, sort, page und die Filter der Aktion. Unbekannte Felder werden abgelehnt. */
export function listInput<const K extends string, F extends z.ZodRawShape = Record<never, never>>(
  spec: ListInputSpec<K, F>,
) {
  const defaultSort: ListSort<K> = { by: spec.defaultSort?.by ?? spec.sortBy[0], dir: spec.defaultSort?.dir ?? 'asc' }
  return z.strictObject({
    q: zListQuery,
    sort: z
      .strictObject({ by: z.enum(spec.sortBy), dir: z.enum(SORT_DIRECTIONS).default('asc') })
      .default(defaultSort as never),
    page: zListPage,
    ...(spec.filters ?? ({} as F)),
  })
}

/** Ausgabeschema einer Listen-Aktion. `total` nur liefern, wenn die Zählung billig ist. */
export function listOutput<R extends z.ZodType>(row: R) {
  return z.object({
    rows: z.array(row),
    nextCursor: z.string().nullable(),
    total: z.int().nonnegative().optional(),
  })
}

export interface ListResult<R> {
  rows: R[]
  nextCursor: string | null
  total?: number
}

// ---- Cursor ------------------------------------------------------------------------------------------
// Undurchsichtig für Aufrufer: eine JSON-Liste der Sortierwerte der letzten Zeile (als Text, damit Zeitstempel
// ihre volle Genauigkeit behalten). Der Server prüft Form und Länge; ein unpassender Cursor ist ein Eingabefehler.

export function encodeCursor(values: readonly (string | null)[]): string {
  return JSON.stringify(values)
}

/** Liefert die Sortierwerte oder null, wenn der Cursor nicht die erwartete Form hat. */
export function decodeCursor(cursor: string, length: number): (string | null)[] | null {
  try {
    const parsed: unknown = JSON.parse(cursor)
    if (!Array.isArray(parsed) || parsed.length !== length) return null
    if (!parsed.every((value) => value === null || typeof value === 'string')) return null
    return parsed as (string | null)[]
  } catch {
    return null
  }
}
